import { useState, useCallback, useMemo, memo } from "react";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { MapPin, DollarSign, Clock, Mail, Phone, Lock, Loader2, AlertTriangle, Heart, User } from "lucide-react";
import { toast } from "sonner";
import type { Id } from "../../../convex/_generated/dataModel";
import { ReportDialog } from "@/components/ReportDialog";
import { SafetyBanner, PersonalConnectionWarning } from "@/components/SafetyBanner";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";

// Memoized thumbnail component
const ImageThumbnail = memo(function ImageThumbnail({
  src,
  isSelected,
  index,
  onSelect,
}: {
  src: string;
  isSelected: boolean;
  index: number;
  onSelect: (src: string) => void;
}) {
  const handleClick = useCallback(() => {
    onSelect(src);
  }, [src, onSelect]);

  return (
    <button
      onClick={handleClick}
      className={`w-20 h-20 rounded-md overflow-hidden border-2 transition-colors flex-shrink-0 ${
        isSelected ? 'border-primary' : 'border-border'
      }`}
    >
      <img 
        src={src} 
        alt={`Thumbnail ${index + 1}`} 
        className="w-full h-full object-cover" 
        loading="lazy"
      />
    </button>
  );
});

// Currency formatter (created once)
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export default function AdDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [responseMessage, setResponseMessage] = useState("");
  const [mainImage, setMainImage] = useState<string | null>(null);

  // Memoize query args
  const adIdArg = useMemo(() => ({ id: id as Id<"ads"> }), [id]);
  const favoriteArg = useMemo(() => ({ adId: id as Id<"ads"> }), [id]);

  // Convex queries and mutations
  const ad = useQuery(api.ads.getAdById, adIdArg);
  const createResponseMutation = useMutation(api.interactions.createAdResponse);
  const toggleFavoriteMutation = useMutation(api.favorites.toggleFavorite);
  const isFavorited = useQuery(api.favorites.isFavorited, favoriteArg);

  // Memoized images array
  const images = useMemo(() => ad?.images || [], [ad?.images]);
  
  // Set initial main image when ad loads
  const displayImage = mainImage ?? images[0] ?? null;

  // Stable callback for response message
  const handleResponseChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setResponseMessage(e.target.value);
  }, []);

  // Stable callback for image selection
  const handleImageSelect = useCallback((src: string) => {
    setMainImage(src);
  }, []);

  // Stable callback for form submission
  const handleSubmitResponse = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await createResponseMutation({
        adId: id as Id<"ads">,
        message: responseMessage,
      });
      setResponseMessage("");
      toast.success(t("postAd.success"));
    } catch (error) {
      toast.error(t("errors.networkError"));
    }
  }, [createResponseMutation, id, responseMessage, t]);

  // Stable callback for favorite toggle
  const handleToggleFavorite = useCallback(async () => {
    try {
      await toggleFavoriteMutation({ adId: id as Id<"ads"> });
    } catch (error) {
      toast.error(t("errors.networkError"));
    }
  }, [toggleFavoriteMutation, id, t]);

  // Memoize formatted date
  const formattedDate = useMemo(() => 
    ad ? new Date(ad.createdAt).toLocaleDateString() : '',
    [ad?.createdAt]
  );

  if (ad === undefined) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-8 text-center">
          <p className="text-muted-foreground mb-4">{t("errors.notFound")}</p>
          <Button asChild>
            <Link href="/browse">
              <a>{t("common.back")}</a>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container py-8">
        {/* Safety Banner for Personal Connections */}
        {ad.isPersonalConnection && <PersonalConnectionWarning />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Images */}
            <div className="card-elegant overflow-hidden mb-8">
              <div className="w-full h-96 bg-muted flex items-center justify-center">
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={ad.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-muted-foreground">No image available</div>
                )}
              </div>

              {images.length > 1 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {images.map((img: string, idx: number) => (
                    <ImageThumbnail
                      key={idx}
                      src={img}
                      index={idx}
                      isSelected={displayImage === img}
                      onSelect={handleImageSelect}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="card-elegant p-6 mb-8">
              <div className="flex items-start justify-between mb-4">
                <h1 className="flex-1">{ad.title}</h1>
                <ReportDialog 
                  adId={id as Id<"ads">} 
                  trigger={
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Report
                    </Button>
                  }
                />
              </div>

              <div className="space-y-4 mb-6">
                {ad.price && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <span className="text-2xl font-bold text-primary">
                      {currencyFormatter.format(ad.price)}
                    </span>
                  </div>
                )}

                {ad.location && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MapPin className="w-5 h-5" />
                    <span>{ad.location}</span>
                  </div>
                )}

                <div className="flex items-center gap-3 text-muted-foreground">
                  <Clock className="w-5 h-5" />
                  <span>{formattedDate}</span>
                </div>

                {ad.isPersonalConnection && (
                  <div className="flex items-center gap-3 text-accent">
                    <Lock className="w-5 h-5" />
                    <span className="font-medium">Token-Gated Listing</span>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-6">
                <h3 className="font-semibold mb-3">Description</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{ad.description}</p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Seller Info */}
            <div className="card-elegant p-6 mb-6">
              <h3 className="font-semibold mb-4">{t('user.sellerInfo')}</h3>
              <div className="flex items-center gap-4 mb-4">
                 <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
                    <User className="h-6 w-6 text-muted-foreground" />
                 </div>
                 <div>
                    <h4 className="font-medium">{t('user.privateSeller')}</h4>
                    <Link href={`/users/${ad.userId}`}>
                        <a className="text-sm text-primary hover:underline">{t('user.viewProfile')}</a>
                    </Link>
                 </div>
              </div>
              <Button asChild variant="outline" className="w-full h-9">
                 <Link href={`/users/${ad.userId}`}>{t('user.otherAds')}</Link>
              </Button>
            </div>

            {/* Contact Card */}
            <div className="card-elegant p-6 mb-6 sticky top-24">
              <h3 className="font-semibold mb-4">Contact Seller</h3>

              <div className="space-y-4 mb-6">
                {ad.contactEmail && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <a
                      href={`mailto:${ad.contactEmail}`}
                      className="text-primary hover:underline break-all"
                    >
                      {ad.contactEmail}
                    </a>
                  </div>
                )}

                {ad.contactPhone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <a
                      href={`tel:${ad.contactPhone}`}
                      className="text-primary hover:underline"
                    >
                      {ad.contactPhone}
                    </a>
                  </div>
                )}
              </div>

              {/* Response Form - Only for signed in users */}
              <SignedIn>
                <form onSubmit={handleSubmitResponse} className="space-y-4">
                  <Textarea
                    placeholder="Send a message to the seller..."
                    value={responseMessage}
                    onChange={handleResponseChange}
                    className="resize-none"
                    rows={4}
                  />
                  <Button type="submit" className="w-full">
                    Send Message
                  </Button>
                </form>
              </SignedIn>

              <SignedOut>
                <div className="space-y-4">
                  <Textarea
                    placeholder="Send a message to the seller..."
                    disabled
                    className="resize-none opacity-60"
                    rows={4}
                  />
                  <Button asChild className="w-full">
                    <Link href="/sign-in">Sign In to Message</Link>
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    <Link href="/sign-up" className="text-primary hover:underline">
                      Create an account
                    </Link>
                    {' '}to contact sellers
                  </p>
                </div>
              </SignedOut>
            </div>

            {/* Stats Card */}
            <div className="card-elegant p-6 mb-6">
              <h3 className="font-semibold mb-4">Listing Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Views</span>
                  <span className="font-medium">{ad.views}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="font-medium capitalize">{ad.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Posted</span>
                  <span className="font-medium">
                    {formattedDate}
                  </span>
                </div>
              </div>
            </div>

            {/* Safety Banner */}
            <SafetyBanner 
              variant="compact" 
              showOnPersonalConnection={ad.isPersonalConnection} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}
